import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { MergeTagPickerComponent } from '../merge-tag-picker.component';
import { MergeTagPickerOverlayService, MergeTagPickerOverlayViewState } from '../merge-tag-picker-overlay.service';

@Component({
	selector: 'app-merge-tag-picker-overlay',
	templateUrl: './merge-tag-picker-overlay.component.html',
	styleUrls: ['./merge-tag-picker-overlay.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MergeTagPickerOverlayComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild(MergeTagPickerComponent)
	private readonly picker?: MergeTagPickerComponent;

	state: MergeTagPickerOverlayViewState = {
		visible: false,
		data: null,
		config: null
	};

	private subscription = new Subscription();
	private dragCleanup?: () => void;

	constructor(
		private readonly overlayService: MergeTagPickerOverlayService,
		private readonly cdr: ChangeDetectorRef,
		private readonly elementRef: ElementRef<HTMLElement>
	) {}

	ngOnInit(): void {
		this.subscription.add(
			this.overlayService.viewState$.subscribe(state => {
				this.state = state;
				this.cdr.markForCheck();
				if (state.visible) {
					requestAnimationFrame(() => this.attachDragHandle());
				}
			})
		);

		this.overlayService.registerComponent(this);
	}

	ngAfterViewInit(): void {
		if (this.state.visible) {
			this.attachDragHandle();
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.detachDragHandle();
		this.overlayService.unregisterComponent(this);
	}

	focusSearchInput(): void {
		if (this.picker) {
			this.picker.focusSearchInput();
			return;
		}

		requestAnimationFrame(() => {
			this.picker?.focusSearchInput();
		});
	}

	onTagSelected(value: string): void {
		this.overlayService.handleTagSelected(value);
	}

	onClosed(): void {
		this.overlayService.handleRequestClose();
	}

	onInvalid(message: string): void {
		this.overlayService.handleInvalid(message);
	}

	private attachDragHandle(): void {
		const host = this.elementRef.nativeElement;
		const handle = host.querySelector('[data-merge-tag-drag-handle="true"]') as HTMLElement | null;
		if (!handle) {
			return;
		}

		this.detachDragHandle();

		const pointerDown = (event: PointerEvent) => {
			if (event.button !== 0) {
				return;
			}

			const target = event.target as HTMLElement | null;
			if (target?.closest('input, textarea, button, mat-icon-button, mat-form-field')) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const initialRect = host.getBoundingClientRect();
			const startX = event.clientX;
			const startY = event.clientY;

			const pointerMove = (moveEvent: PointerEvent) => {
				const deltaX = moveEvent.clientX - startX;
				const deltaY = moveEvent.clientY - startY;

				this.overlayService.updateManualPosition({
					left: initialRect.left + deltaX,
					top: initialRect.top + deltaY
				});
			};

			const pointerUp = () => {
				window.removeEventListener('pointermove', pointerMove, true);
				window.removeEventListener('pointerup', pointerUp, true);
			};

			window.addEventListener('pointermove', pointerMove, true);
			window.addEventListener('pointerup', pointerUp, true);
		};

		handle.style.cursor = 'move';
		handle.style.touchAction = 'none';
		handle.addEventListener('pointerdown', pointerDown);

		this.dragCleanup = () => {
			handle.removeEventListener('pointerdown', pointerDown);
		};
	}

	private detachDragHandle(): void {
		if (this.dragCleanup) {
			this.dragCleanup();
			this.dragCleanup = undefined;
		}
	}
}
